import { ReactNode, useState, TouchEvent, DragEvent } from 'react';

interface SwipeableProps {
    children: ReactNode,
    onSwipeLeft?: () => void,
    onSwipeRight?: () => void,
    cooldown?: number,
    // TODO vertical swiping? i don't think we used/wanted this but it was baked into the jQuery library
}

interface CoordsSource {
    clientX: number,
    clientY: number,
}

const isTouchEvent = (e: TouchEvent | DragEvent): e is TouchEvent => {
    return e.hasOwnProperty('targetTouches');
};
const getCoordsSource = (e: TouchEvent | DragEvent): CoordsSource => {
    let coordsSource;
    if (isTouchEvent(e)) {
        coordsSource = e.targetTouches[0];
    } else {
        coordsSource = e;
    }

    return coordsSource;
};

const MINIMUM_SWIPE_DISTANCE = 50;

// Sources: https://stackoverflow.com/questions/70612769/how-do-i-recognize-swipe-events-in-react and its answers plus
// https://github.com/jquery-archive/jquery-mobile/blob/main/js/events/touch.js .
//
// Arguably a lot of what's done with state here is DOM-related and might make sense as refs?
//
// TODO The touch/mousemove handlers fire too often, and if we had more than one caller it might make sense to put the
//  work in to make this throttle appropriately.
export const Swipeable = ({ children, onSwipeLeft, onSwipeRight, cooldown: cooldownParam }: SwipeableProps) => {
    const [touchStartX, setTouchStartX] = useState<number | null>(null);
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [touchEndX, setTouchEndX] = useState<number | null>(null);
    const [touchEndY, setTouchEndY] = useState<number | null>(null);
    // Mouseout may spuriously fire more than once (our first caller moves the div on-mouseout!). Use this to throttle.
    //
    // In theory this could have been done via lodash debounce, but in practice that requires React.useMemo, and *that*
    // requires very careful reasoning about dependencies across setter functions.
    const [lastSwipeTime, setLastSwipeTime] = useState<Date | null>(null);

    const cooldown = cooldownParam ?? 1000;
    const onTouchStart = (e: TouchEvent<HTMLDivElement> | DragEvent<HTMLDivElement>) => {
        const coordsSource = getCoordsSource(e);

        setTouchEndX(null);
        setTouchStartX(coordsSource.clientX);
        setTouchEndY(null);
        setTouchStartY(coordsSource.clientY);
    };

    const onTouchMove = (e: TouchEvent | DragEvent) => {
        const coordsSource = getCoordsSource(e);

        setTouchEndX(coordsSource.clientX);
        setTouchEndY(coordsSource.clientY);
    };

    const onTouchEnd = () => {
        if (touchStartX && touchEndX && (lastSwipeTime === null || new Date().getTime() - lastSwipeTime.getTime() > cooldown)) {
            setLastSwipeTime(new Date());
            swipeHorizontal();
        }
    };

    const swipeHorizontal = () => {
        const xDistance = (touchStartX || 0) - (touchEndX || 0);
        const yDistance = (touchStartY || 0) - (touchEndY || 0);
        if (Math.abs(yDistance) >= Math.abs(xDistance)) {
            return;
        }

        const isLeftSwipe = xDistance > MINIMUM_SWIPE_DISTANCE;
        const isRightSwipe = xDistance < -MINIMUM_SWIPE_DISTANCE;

        if (isLeftSwipe && onSwipeLeft) {
            onSwipeLeft();
        }

        if (isRightSwipe && onSwipeRight) {
            onSwipeRight();
        }
    };

    return (
        <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
            {children}
        </div>
    );
};
